import { useEffect, useRef, type ReactNode } from 'react'
import { useCrisisStore } from '../stores/crisisStore'
import { useAuth } from './useAuth'

interface CrisisProviderProps {
  children: ReactNode
}

export function CrisisProvider({ children }: CrisisProviderProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { refreshActiveCrisis, activeCrisis, initState, setInitState } = useCrisisStore()
  const hasInitializedRef = useRef(false)

  useEffect(() => {
    // Initialize crisis state once when auth is ready and authenticated
    if (isAuthenticated && !authLoading && !hasInitializedRef.current) {
      hasInitializedRef.current = true
      console.debug('[CrisisProvider] Auth ready, initializing crisis state')

      // If crisis was restored from persistence, mark as ready immediately
      if (activeCrisis && initState === 'idle') {
        console.debug('[CrisisProvider] Active crisis restored from persistence, marking ready')
        setInitState('ready')
        return
      }

      // Otherwise fetch active crisis (session logic is now in store)
      if (initState === 'idle') {
        console.debug('[CrisisProvider] Fetching active crisis')
        refreshActiveCrisis()
      }
    }
  }, [isAuthenticated, authLoading, refreshActiveCrisis, activeCrisis, initState, setInitState])

  // Handle logout: reset initialization when auth state changes to false
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      hasInitializedRef.current = false
    }
  }, [isAuthenticated, authLoading])

  return <>{children}</>
}
